import React, { useState, useEffect, useMemo } from 'react';
import { Send, X, Search } from 'lucide-react';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import { useToast } from '../../../../foundation/altzero_base/base/hooks/use-toast';
import { emailApiService } from '../../services/email/emailApiService';
import { format } from 'date-fns';
import { EmailThreadView } from './EmailThreadView';
import { PaginationBar } from './PaginationBar';

const PAGE_SIZE = 20;

interface SentEmailListProps {
    tenderId?: string;
}

export function SentEmailList({ tenderId }: SentEmailListProps) {
    const { toast } = useToast();
    const [messages, setMessages] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);

    useEffect(() => {
        loadSentMessages();
    }, [tenderId]);

    // Reset page when search changes
    useEffect(() => { setPage(1); }, [search]);

    const loadSentMessages = async () => {
        try {
            setLoading(true);
            const data = await emailApiService.getSentMessages({ tenderId });
            setMessages(data);
            setPage(1);
        } catch (error: any) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to load sent messages',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return messages;
        const q = search.toLowerCase();
        return messages.filter(msg =>
            msg.subject?.toLowerCase().includes(q) ||
            msg.recipient_emails?.join(', ').toLowerCase().includes(q) ||
            msg.thread?.retailer?.name?.toLowerCase().includes(q) ||
            msg.thread?.tender?.opportunity_name?.toLowerCase().includes(q)
        );
    }, [messages, search]);

    const paginated = useMemo(() =>
        filtered.slice((page - 1) * pageSize, page * pageSize),
        [filtered, page, pageSize]
    );

    if (selectedThreadId) {
        return (
            <div className="h-full flex flex-col bg-white rounded-lg border">
                <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                    <h3 className="font-semibold text-gray-700">Thread View</h3>
                    <button
                        onClick={() => setSelectedThreadId(null)}
                        className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>
                <div className="flex-1 overflow-hidden">
                    <EmailThreadView
                        threadId={selectedThreadId}
                        onClose={() => setSelectedThreadId(null)}
                    />
                </div>
            </div>
        );
    }

    if (loading) {
        return <div className="text-center py-12 text-gray-500">Loading sent messages...</div>;
    }

    return (
        <div className="flex flex-col h-full">
            {/* Search + Filter bar */}
            <div className="mb-4 flex items-center gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                        placeholder="Search by subject, recipient, retailer, tender..."
                        className="pl-8"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {filtered.length} result{filtered.length !== 1 ? 's' : ''}
                </span>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto">
                {filtered.length === 0 ? (
                    <div className="text-center py-12">
                        <Send className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-600 mb-2">
                            {search ? 'No messages match your search.' : 'No sent emails found'}
                        </p>
                        {!search && (
                            <p className="text-sm text-gray-500">
                                {tenderId ? 'No emails sent for this tender yet' : 'Emails you send to retailers will appear here'}
                            </p>
                        )}
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <tbody>
                            {paginated.map((msg) => (
                                <tr
                                    key={msg.id}
                                    onClick={() => setSelectedThreadId(msg.thread_id)}
                                    className="border-b cursor-pointer hover:bg-blue-50/40 transition-colors group"
                                >
                                    {/* To */}
                                    <td className="py-2.5 pl-4 pr-3 w-48 font-medium text-gray-900 truncate max-w-[12rem]">
                                        {msg.recipient_emails?.[0] || 'Unknown'}
                                        {(msg.recipient_emails?.length ?? 0) > 1 && (
                                            <span className="text-gray-400 font-normal ml-1">+{(msg.recipient_emails?.length ?? 0) - 1}</span>
                                        )}
                                    </td>

                                    {/* Subject + tags */}
                                    <td className="py-2.5 px-2 overflow-hidden">
                                        <div className="flex items-baseline gap-2 truncate">
                                            <span className="font-medium text-gray-800 truncate shrink-0 max-w-[220px] group-hover:text-blue-700">
                                                {msg.subject}
                                            </span>
                                            {msg.thread?.retailer?.name && (
                                                <span className="text-xs text-purple-600 shrink-0 truncate">
                                                    · {msg.thread.retailer.name}
                                                </span>
                                            )}
                                            {msg.thread?.tender?.opportunity_name && (
                                                <span className="text-xs text-blue-500 shrink-0 truncate">
                                                    · {msg.thread.tender.opportunity_name}
                                                </span>
                                            )}
                                        </div>
                                    </td>

                                    {/* Date */}
                                    <td className="py-2.5 pl-2 pr-4 w-36 text-right text-xs text-gray-400 whitespace-nowrap">
                                        {format(new Date(msg.sent_at), 'MMM d, yyyy h:mm a')}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {filtered.length > 0 && (
                <PaginationBar
                    page={page}
                    pageSize={pageSize}
                    total={filtered.length}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                />
            )}
        </div>
    );
}
